<template>
  <v-expansion-panel :id="`${testId}`" v-show="true">
    <v-expansion-panel-header>
      <h2 id="mito-disease-test-title">{{ testName }}</h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="mito-disease-test-grid"
      >
        <v-container>
          <v-row>
            <v-col class="custom-col flex flex-col ">
              <div class="mb-2">
                VCGS offers
                <a
                  href="https://www.vcgs.org.au/tests/mitochondrial-diseases/"
                  target="_blank"
                >
                  Medicare-funded
                  <strong>Whole Genome Sequencing (WGS)</strong></a
                >
                which includes analysis of mitochondrial genome (mtDNA) for MBS
                Item Numbers 73456 (singleton) and 73457 (trio).
              </div>
              <div class="mb-2">
                Characterisation of germline variants present in nuclear and
                mitochondrial DNA is performed via whole genome sequencing and
                analysis. This WGS test includes analysis of genes in the
                <strong
                  >PanelApp Australia
                  <a href="https://panelapp-aus.org/panels/203/" target="_blank"
                    >Mitochondrial disease</a
                  >
                  gene panel.</strong
                >
                Analysis is phenotype driven and may include additional
                phenotype specific genes at the discretion of the laboratory.
              </div>
              <div class="mb-2">
                To qualify for the Medicare rebate, these tests must be ordered
                by a specialist physician (e.g. clinical or metabolic
                geneticist, neurologist, metabolic physician, or
                ophthalmologist) and meet the Medicare inclusion criteria (see
                below).
              </div>
              <v-alert
                text
                outlined
                icon="mdi-cash-usd-outline"
                type="warning"
                class="mb-6"
              >
                If Medicare billing is not applicable to your clinic or patient,
                please select alternate funding source in the relevant field
                below.
              </v-alert>
              <div class="mb-2">
                <strong>
                  Turnaround time
                </strong>
              </div>
              <div class="mb-2">
                <strong>
                  Trio:
                </strong>
                8-10 weeks
              </div>
              <div class="mb-2">
                <strong>
                  Singleton:
                </strong>
                3-4 months
              </div>
            </v-col>
          </v-row>
          <v-row id="medicare-inclusion-criteria">
            <v-col class="custom-col">
              <h3 class="mb-2 red--text">
                Medicare item number 73456 & 73457 inclusion criteria*
              </h3>
              <div class="mb-2">
                As requested by a specialist or consultant physician for a
                patient with a strong suspicion of a mitochondrial disease,
                based on the onset of at least one or more of the following
                clinical features:
              </div>
              <div>
                <ul>
                  <li v-for="(criterion, idx) in medicareCriteria" :key="idx">
                    {{ criterion.content }}
                  </li>
                </ul>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="1">
              <v-checkbox
                v-model="acknowledgeTC"
                :data-testid="testId + '-tc-checkbox'"
                hide-details
              />
            </v-col>
            <v-col cols="11">
              <span style="display: inline-block;" class="ml-md-n4 ml-xl-n16">
                By checking this box, I acknowledge that there is a strong
                suspicion of a mitochondrial disease in this patient based on
                the onset of at least one or more of the above clinical features
              </span>
            </v-col>
          </v-row>
          <v-row
            class="red--text"
            style="margin-top: -4px;"
            v-if="!acknowledgeTC"
          >
            <v-col class="custom-col">
              Terms and conditions must be acknowledged
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Funding source*</span>
              <v-select
                :disabled="!acknowledgeTC"
                item-disabled=""
                v-model="billTo"
                :items="billingOptions"
                item-text="key"
                item-value="value"
                :id="testId + '.billTo'"
                outlined
                :rules="billingRule"
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Family type*</span>
              <v-select
                :disabled="!acknowledgeTC"
                v-model="familyType"
                :items="familyTypes"
                :id="testId + '.familyType'"
                :rules="ruleNotNull"
                outlined
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <b class="text-title">Clinical features</b>
              <template v-if="brokenPhenotypesApi">
                <BrokenApiAlert type="phenotypes" />
              </template>
              <template v-else>
                <v-autocomplete
                  :disabled="!acknowledgeTC"
                  v-model="phenotypes"
                  :id="testId + '.phenotypes'"
                  :items="phenotypeList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  deletable-chips
                  @input="searchInputPhenotypes = null"
                  :search-input.sync="searchInputPhenotypes"
                  hide-details="auto"
                  outlined
                  class="autocomplete-label"
                  @update:search-input="handlePhSearchChange"
                  :filter="filterPhItems"
                >
                  <template v-slot:prepend-item v-if="isSearchingHp">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ phSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span class="text-hint"
                  >Please start typing the main clinical features. e.g.
                  intellectual disability. Select the most appropriate HPO term
                  from those suggested. Detailed clinical information helps us
                  diagnose patients more accurately. The phenotype's are sourced
                  from hpo.jax.org.</span
                >
              </template>
              <template v-if="badSearchQuery">
                <BadSearchQueryAlert />
              </template>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Clinical notes*</span>
              <v-textarea
                :disabled="!acknowledgeTC"
                rows="2"
                auto-grow
                v-model="clinicalNotes"
                :id="testId + '.clinicalNotes'"
                :rules="ruleNotNull"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >Please provide detailed clinical information as evidence of
                strong clinical suspicion of Mitochondrial disease as per the
                Medicare criteria above.</span
              >
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Family history</span>
              <v-textarea
                :disabled="!acknowledgeTC"
                rows="1"
                auto-grow
                v-model="familyHx"
                :id="testId + '.familyHx'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. results of relevant prior genetic and non-genetic
                investigations, biochemistry, histopathology and imaging,
                etc.</span
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <FamilyMember
                v-if="isTrio"
                title="Family member 1"
                :idPrefix="testId + '.fm0.'"
                ref="familyMember1"
                :patientdata="patientdata"
                :familytype="familyType"
                :populatekeys="[
                  'surname',
                  'country',
                  'unitFlat',
                  'streetAddress',
                  'suburb',
                  'postcode',
                  'state',
                ]"
                testType="mito_diagnostic"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <FamilyMember
                v-if="isTrio"
                style="grid-area: person2"
                title="Family member 2"
                :idPrefix="testId + '.fm1.'"
                ref="familyMember2"
                :patientdata="patientdata"
                :familytype="familyType"
                :populatekeys="[
                  'surname',
                  'country',
                  'unitFlat',
                  'streetAddress',
                  'suburb',
                  'postcode',
                  'state',
                ]"
                testType="mito_diagnostic"
              />
            </v-col>
          </v-row>
        </v-container>

        <MitoDiseaseConsentContainer
          ref="mitoDiseaseConsent"
          :isTrio="isTrio"
          :force-form-dirty="forceFormDirty"
          :testName="testName"
          :testId="testId"
        />

        <TestPriceContainer :price="price" />
      </v-form>
      <v-btn
        class="primary-btn btn-padding-medium top-spaced-item"
        @click="goToNextSection()"
      >
        Continue to next section
      </v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import FamilyMember from "../FamilyMember.vue";
import PhenotypesMixin from "../../mixins/PhenotypesMixin";
import GenesMixin from "../../mixins/GenesMixin.vue";
import FormRulesMixin from "../../mixins/FormRulesMixin";
import TestPriceContainer from "../common/TestPriceContainer";
import MitoDiseaseConsentContainer from "./MitoDiseaseConsentContainer";
import { EventBus } from "@/event-bus";
import { useSectionStore } from "../../store/SectionStore";
import RetrieveJSONMixin from "../../mixins/RetrieveJSONMixin.vue";
import BrokenApiAlert from "../common/BrokenApiAlert.vue";
import BadSearchQueryAlert from "../common/BadSearchQueryAlert.vue";

const MEDICARE_CRITERIA = [
  {
    content: `meeting the clinical criteria of a probable indicator of mitochondrial disease on a relevant scoring system`,
  },
  { content: `evident mitochondrial dysfunction or decompensation` },
  {
    content: `unexplained hypotonia or weakness, profound hypoglycaemia or ‘failure to thrive’ in the presence of a metabolic acidosis`,
  },
  {
    content: `unexplained single or multi-organ dysfunction or fulminant failure (including, but not limited to, neuropathies, myopathies, 
    hepatopathy, pancreatic and/or bone marrow failure)`,
  },
  {
    content: `refractory or atypical seizures, developmental delays or cognitive regression, or progressive encephalopathy or progressive 
    encephalomyopathy`,
  },
  { content: `cardiomyopathy and/or cardiac arrythmias` },
  { content: `rapid hearing or painless visual loss or ptosis` },
  { content: `stroke-like episodes or nonvasculitic strokes` },
  {
    content: `ataxia, encephalopathy, seizures, muscle fatigue or weakness`,
  },
  { content: `external ophthalmoplegia` },
  {
    content: `hearing loss, diabetes, unexplained short stature, or endocrinopathy`,
  },
];

const FAMILY_TYPES = ["Singleton", "Trio"];
const PREDETERMINED_GENE_PANELS = ["Mitochondrial disease"];
const PREDETERMINED_MT_DNA = true;

export default {
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
    forceFormDirty: {
      default: false,
      type: Boolean,
    },
  },
  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.mitoDiseaseTestPricing = await this.execRetrieveJson(
      "json/pricing/mito_diagnostic_pricing.json",
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    EventBus.$emit("getPatientData");

    const element = document.getElementById("mito-disease-test-title");

    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }

    this.urgency = "Standard";
    this.analysisType = "Comprehensive Genome";
  },
  beforeDestroy() {
    EventBus.$emit("deselectTrioTest", this.testName);
  },
  mixins: [FormRulesMixin, PhenotypesMixin, RetrieveJSONMixin, GenesMixin],
  name: "",
  components: {
    TestPriceContainer,
    MitoDiseaseConsentContainer,
    FamilyMember,
    BrokenApiAlert,
    BadSearchQueryAlert,
  },
  data: () => ({
    valid: false,
    urgent: false,
    urgency: null,
    urgentReason: "",
    familyType: null,
    analysisType: null,
    genePanels: PREDETERMINED_GENE_PANELS,
    phenotypes: [],
    familyHx: "",
    clinicalNotes: "",
    mitoDiseaseTestPricing: null,
    price: null,
    testCode: null,
    cgApproved: true,
    cgDetails: "",
    medicare: null,
    indGenes: [],
    billTo: "",
    searchInputPhenotypes: null,
    medicareCriteria: MEDICARE_CRITERIA,
    mtDna: PREDETERMINED_MT_DNA,
    familyTypes: FAMILY_TYPES,
    acknowledgeTC: false,
    billingOptions: [
      { key: "Medicare Eligible (Bulk Bill)", value: "MEDICARE" },
      { key: "Patient / Family", value: "PATIENT" },
      { key: "Clinical Service / Institution / Hospital", value: "OTHER" },
    ],
  }),
  watch: {
    billTo: function(newVal) {
      if (this.billTo === "MEDICARE") {
        useSectionStore().deligateBillingAddress = null;
        this.medicare = true;
      } else if (this.billTo === "PATIENT") {
        useSectionStore().deligateBillingAddress = "PATIENT";
        this.medicare = false;
      } else {
        useSectionStore().deligateBillingAddress = "OTHER";
        this.medicare = false;
      }

      EventBus.$emit("medicareMitoDiseaseStatus", this.billTo === "MEDICARE");
    },
    pricing: function(newVal) {
      // Update testCode
      this.testCode = newVal.testCode;

      // Update quotedPrice and totalPrice
      this.price = newVal.price;
      this.$emit("toggleTotalPrice", this.pricing.price, this.testId);
    },
    familyType: function(newVal) {
      // When it is Trio test
      if (newVal === "Trio") {
        // update selectedTrioTests array in App component
        EventBus.$emit("selectTrioTest", this.testName);
      } else {
        // when it is singleton test, update selectedTrioTests in App component
        EventBus.$emit("deselectTrioTest", this.testName);
      }
    },
  },
  computed: {
    isTrio: function() {
      return this.familyType === "Trio";
    },
    pricing: function() {
      if (!this.familyType) return null;

      if (this.billTo === "MEDICARE") {
        return this.mitoDiseaseTestPricing["medicare"][this.familyType][
          this.analysisType
        ];
      } else {
        return this.mitoDiseaseTestPricing[this.familyType][this.analysisType];
      }
    },
    billingRule() {
      if (this.billTo === "MEDICARE") {
        if (
          this.patientdata["patientStatusContent"] ===
            "public_outpatient_public_hospital" ||
          this.patientdata["patientStatusContent"] === "public_public_hospital"
        ) {
          return ["You cannot order a Medicare test for a public patient."];
        }

        return [];
      } else {
        return this.ruleNotNull;
      }
    },
  },
  methods: {
    isFormValid: function() {
      // Check T&C acknowledgement checkboxe
      if (!this.acknowledgeTC) {
        return false;
      }

      // Check consent checkboxes
      let isValid;
      if (typeof this.$refs.mitoDiseaseConsent !== "undefined") {
        isValid = this.$refs.mitoDiseaseConsent.isFormValid();
      } else isValid = false;

      if (this.isTrio) {
        return (
          isValid &&
          this.$refs.familyMember1.isFormValid() &&
          this.$refs.familyMember2.isFormValid()
        );
      } else return isValid;
    },

    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
    transferData: function() {
      let familyDetails = [];

      for (let [familyKey, familyComponent] of Object.entries(this.$refs)) {
        if (familyComponent && familyKey.substring(0, 12) === "familyMember") {
          familyDetails.push(familyComponent.transferData());
        }
      }

      // Convert hardcoded gene panels value to "<panelId> - <panelName>"
      const finalGenePanels =
        this.genePanels[0] === "Mitochondrial disease"
          ? "203 - Mitochondrial disease"
          : "";

      let result = {
        suspicionAck: this.acknowledgeTC,
        urgent: this.urgent,
        urgency: this.urgency,
        urgentReason: this.urgentReason,
        testType: this.testId,
        familyType: this.familyType,
        analysisType: this.analysisType,
        genePanels: finalGenePanels,
        phenotypes: this.getFinalPhenotypes(this.phenotypes),
        familyHx: this.familyHx,
        clinicalNotes: this.clinicalNotes,
        family: familyDetails,
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        cgApproved: this.cgApproved,
        cgDetails: this.cgDetails,
        medicare: this.medicare,
        indGenes: this.getFinalIndGenes(this.indGenes),
        billTo: this.billTo,
        mtDna: this.mtDna,
      };

      // Consent data is present, except if the test is medicare but not approved by gc
      if (this.$refs.mitoDiseaseConsent) {
        const consentData = this.$refs.mitoDiseaseConsent.transferData();
        // assign the data fields from consent direct into the top level of our data
        Object.assign(result, consentData);
      }

      return result;
    },
  },
};
</script>

<style scoped>
.autocomplete-label label {
  background: red;
  border: 2px solid red;
  font-size: 12px;
}
</style>
